import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectGameId } from "features/gamestate/gameStateSlice";
import Analytics from "analytics";
import { EVENTS } from "analytics/events";
import {
  selectClue,
  selectPanelMode,
  selectSelectedQuestionId,
} from "./editorSlice";
import "./roundEditor.css";

const BASE_VALUES = [200, 400, 600, 800, 1000];

// Round 1 = base, Round 2 = double, Round 3 = triple ("Double/Triple Groupardy").
const roundMultiplier = (round) => {
  const m = /^round(\d+)$/.exec(round || "");
  return m ? Number(m[1]) : 1;
};

const EditorValueCell = ({ question, questionId, rowIndex, round, isFinal = false }) => {
  const dispatch = useDispatch();
  const panelMode = useSelector(selectPanelMode);
  const selectedQuestionId = useSelector(selectSelectedQuestionId);
  const gameId = useSelector(selectGameId);

  // The cell's identity comes from the board's category map, so empty (and
  // not-yet-loaded) cells are still selectable for authoring.
  const qid = questionId || question?.id;
  const isSelected = panelMode === "clue" && selectedQuestionId === qid;

  const hasClue = !!question?.clue?.trim();
  const hasAnswer = Array.isArray(question?.answers)
    ? question.answers.some((a) => a?.trim())
    : false;
  const isEmpty = !hasClue;
  const isDD = !isFinal && !!question?.dailyDouble;
  const value = isFinal ? null : BASE_VALUES[rowIndex] * roundMultiplier(round);

  const handleClick = () => {
    if (!qid) return;
    const opening = !(panelMode === "clue" && selectedQuestionId === qid);
    dispatch(selectClue({ questionId: qid }));
    if (opening) {
      Analytics.track(EVENTS.CLUE_EDIT_OPENED, {
        game_id: gameId,
        round,
        question_id: qid,
        was_empty: isEmpty,
      });
    }
  };

  const classes = [
    "re-value-cell",
    isEmpty ? "is-empty" : "",
    isDD ? "is-dd" : "",
    isSelected ? "is-selected" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const badgeClass = isFinal || isDD ? "re-dd-badge" : "re-value-badge";
  const badgeLabel = isFinal ? "★ WAGER" : isDD ? "★ DAILY DOUBLE" : `$${value}`;

  return (
    <div
      className={classes}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleClick()}
      aria-pressed={isSelected}
    >
      <div className={`${badgeClass} re-badge-row`}>
        <span className="re-badge-label">{badgeLabel}</span>
        {isEmpty && <span className="re-empty-pill">EMPTY</span>}
      </div>
      {hasClue ? (
        <>
          <div className="re-cell-clue">{question.clue}</div>
          {hasAnswer && (
            <div className="re-cell-answer">↳ {question.answers[0]}</div>
          )}
        </>
      ) : (
        <div className="re-empty-cta">＋ Write clue</div>
      )}
    </div>
  );
};

export default EditorValueCell;
